import React from "react";

import { makeStyles } from "@material-ui/core/styles";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import { Checkbox, IconButton, ListItemText, ListSubheader, Tooltip } from "@material-ui/core";
import FilterListIcon from "@material-ui/icons/FilterList";
import SelectAllIcon from "@material-ui/icons/SelectAll";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
	selectAllItem: {
		borderBottom: `1px solid ${theme.palette.divider}`,
		cursor: "pointer",
		lineHeight: "normal",
		marginBottom: 4,
		paddingBottom: 4,
		paddingTop: 4,
		"&:hover": {
			backgroundColor: theme.palette.action.hover,
		},
	},
	menuItem: {
		"& $onlyButton": {
			opacity: 0,
			pointerEvents: "none",
		},
		"&:hover $onlyButton, &:focus-within $onlyButton": {
			opacity: 1,
			pointerEvents: "auto",
		},
		"@media (hover: none), (pointer: coarse)": {
			"& $onlyButton": {
				opacity: 1,
				pointerEvents: "auto",
			},
		},
	},
	menuItemContent: {
		display: "flex",
		alignItems: "center",
		width: "100%",
		minWidth: 0,
	},
	itemText: {
		flex: 1,
		minWidth: 0,
	},
	selectAllIcon: {
		color: theme.palette.text.secondary,
		marginLeft: 8,
		marginRight: 11,
	},
	onlyButton: {
		borderRadius: 4,
		boxShadow: "none",
		color: theme.palette.text.secondary,
		backgroundColor: theme.palette.action.hover,
		height: 32,
		marginLeft: theme.spacing(1),
		padding: 4,
		transition: theme.transitions.create(["background-color", "opacity"], {
			duration: theme.transitions.duration.shortest,
		}),
		width: 32,
		"&:hover": {
			boxShadow: "none",
			backgroundColor: theme.palette.action.selected,
		},
	},
}));

const TicketsQueueSelect = ({
	userQueues,
	selectedQueueIds = [],
	includeNoQueue = false,
	onChange,
	onSelectOnly,
	onSelectNoQueueOnly,
	onToggleNoQueue,
	onSelectAll,
	onClearAll,
}) => {
	const classes = useStyles();
	const queueIds = (userQueues || [])
		.map(queue => Number(queue.id))
		.filter(queueId => Number.isFinite(queueId));
	const selectedQueueIdSet = new Set(
		(selectedQueueIds || []).map(queueId => Number(queueId))
	);
	const allSelected =
		queueIds.length > 0 &&
		queueIds.every(queueId => selectedQueueIdSet.has(queueId)) &&
		includeNoQueue;
	const toggleAllTooltip = allSelected
		? i18n.t("ticketsQueueSelect.clearAllTooltip")
		: i18n.t("ticketsQueueSelect.selectAllTooltip");
	const selectedValues = includeNoQueue
		? [...selectedQueueIds, "__no_queue"]
		: selectedQueueIds;

	const handleChange = e => {
		const selectedValues = e.target.value || [];

		if (selectedValues.includes("__all")) {
			if (allSelected && onClearAll) {
				onClearAll();
			} else if (onSelectAll) {
				onSelectAll();
			}
			return;
		}

		const nextIncludeNoQueue = selectedValues.includes("__no_queue");
		if (nextIncludeNoQueue !== includeNoQueue && onToggleNoQueue) {
			onToggleNoQueue();
		}

		onChange(
			selectedValues
				.map(value => Number(value))
				.filter(value => Number.isFinite(value))
		);
	};

	const handleSelectNoQueueOnly = event => {
		event.preventDefault();
		event.stopPropagation();
		if (onSelectNoQueueOnly) {
			onSelectNoQueueOnly();
		}
	};

	const handleSelectOnly = (event, queueId) => {
		event.preventDefault();
		event.stopPropagation();
		if (onSelectOnly) {
			onSelectOnly(queueId);
		}
	};

	const handleSelectAll = event => {
		event.preventDefault();
		event.stopPropagation();
		if (allSelected && onClearAll) {
			onClearAll();
		} else if (onSelectAll) {
			onSelectAll();
		}
	};

	return (
		<div style={{ width: "100%", minWidth: 0, marginTop: -4 }}>
			<FormControl fullWidth margin="dense">
				<Select
					multiple
					displayEmpty
					variant="outlined"
					value={selectedValues}
					onChange={handleChange}
					MenuProps={{
						anchorOrigin: {
							vertical: "bottom",
							horizontal: "left",
						},
						transformOrigin: {
							vertical: "top",
							horizontal: "left",
						},
						getContentAnchorEl: null,
					}}
					renderValue={() => i18n.t("ticketsQueueSelect.placeholder")}
				>
					<ListSubheader
						className={classes.selectAllItem}
						disableSticky
						onMouseDown={event => {
							event.preventDefault();
							event.stopPropagation();
						}}
						onClick={handleSelectAll}
					>
						<div className={classes.menuItemContent}>
							<SelectAllIcon className={classes.selectAllIcon} fontSize="small" />
							<ListItemText
								className={classes.itemText}
								primary={i18n.t("ticketsQueueSelect.all")}
							/>
							<Tooltip title={toggleAllTooltip}>
								<IconButton
									aria-label={toggleAllTooltip}
									className={classes.onlyButton}
									onMouseDown={event => {
										event.preventDefault();
										event.stopPropagation();
									}}
									onClick={handleSelectAll}
									size="small"
								>
									<FilterListIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</div>
					</ListSubheader>
					<MenuItem className={classes.menuItem} dense value="__no_queue">
						<div className={classes.menuItemContent}>
							<Checkbox
								size="small"
								color="primary"
								checked={includeNoQueue}
							/>
							<ListItemText
								className={classes.itemText}
								primary={i18n.t("ticketsQueueSelect.noQueue")}
							/>
							<Tooltip
								title={
									includeNoQueue && selectedQueueIds.length === 0
										? i18n.t("ticketsQueueSelect.allTooltip")
										: i18n.t("ticketsQueueSelect.onlyTooltip")
								}
							>
								<IconButton
									aria-label={
										includeNoQueue && selectedQueueIds.length === 0
											? i18n.t("ticketsQueueSelect.allTooltip")
											: i18n.t("ticketsQueueSelect.onlyTooltip")
									}
									className={classes.onlyButton}
									onMouseDown={event => {
										event.preventDefault();
										event.stopPropagation();
									}}
									onClick={handleSelectNoQueueOnly}
									size="small"
								>
									<FilterListIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</div>
					</MenuItem>
					{userQueues?.length > 0 &&
						userQueues.map(queue => (
							<MenuItem
								className={classes.menuItem}
								dense
								key={queue.id}
								value={Number(queue.id)}
							>
								<div className={classes.menuItemContent}>
									<Checkbox
										style={{
											color: queue.color,
										}}
										size="small"
										color="primary"
										checked={selectedQueueIds.indexOf(Number(queue.id)) > -1}
									/>
									<ListItemText className={classes.itemText} primary={queue.name} />
									<Tooltip
										title={
											selectedQueueIds.length === 1 &&
											selectedQueueIds[0] === Number(queue.id)
												? i18n.t("ticketsQueueSelect.allTooltip")
												: i18n.t("ticketsQueueSelect.onlyTooltip")
										}
									>
										<IconButton
											aria-label={
												selectedQueueIds.length === 1 &&
												selectedQueueIds[0] === Number(queue.id)
													? i18n.t("ticketsQueueSelect.allTooltip")
													: i18n.t("ticketsQueueSelect.onlyTooltip")
											}
											className={classes.onlyButton}
											onMouseDown={event => {
												event.preventDefault();
												event.stopPropagation();
											}}
											onClick={event => handleSelectOnly(event, queue.id)}
											size="small"
										>
											<FilterListIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								</div>
							</MenuItem>
						))}
				</Select>
			</FormControl>
		</div>
	);
};

export default TicketsQueueSelect;
